import React, { useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitch';
import { getModelTitle } from '../../../services/carList/utils/Car.utils';
import { getTotalPriceObjByType } from '@utils/Price.utils';
import { CarDetailsService, IFeature, IPriceV2Types } from '../../../services';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import Spinner from '@components/Spinner';
import useAccessoryMonthlyPrices from '@hooks/useAccessoryPrices';
import PriceSuffix from '@components/PriceSuffix';
import { ICarDetail } from '../../../redux/carDetails/carDetails.duck.interface';
import CarDetailsDuck from '../../../redux/carDetails/carDetails.duck';
import { useDispatch } from 'react-redux';
import { orderBy } from 'lodash';

const KEY_ALL = 'all';
const KEY_ENGINES = 'engine';
const KEY_COLORS = 'color';
const KEY_INTERIORS = 'interior';
const KEY_FEATURES = 'feature';

export interface IFeaturesStyled {
    className: string;
    carDetails: ICarDetail;
}

const ModalFeaturesTemplate = ({ className, carDetails: trim }: IFeaturesStyled) => {
    const { t } = useTranslations();
    const { formatMoney } = useMoneyFormatter();
    const { getOptionMonthlyPrice } = useAccessoryMonthlyPrices();
    const { carDetails: selectedCarDetails, defaultConfiguration } = useCarDetailsDuck();
    const dispatch = useDispatch();

    const [activeSection, setActiveSection] = useState(KEY_ALL);
    const [activeFeature, setActiveFeature] = useState(KEY_ALL);
    const [exteriorColors, setExteriorColors] = useState(null);
    const [interiorColors, setInteriorColors] = useState(null);
    const [motorizations, setMotorizations] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [carDetails, setCarDetails] = useState(trim);

    const shouldIncludeBodyStyleDescription = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_SHOW_GR_BODY_STYLE_IN_CAR_TITLE
    );
    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    useEffect(() => {
        setIsLoading(true);

        CarDetailsService.getCarDetailsWithAllOptions(
            carDetails.nameplateBodyStyleSlug,
            carDetails.specPackSlug,
            carDetails.engineGearboxFuelSlug,
            carDetails.exteriorColourSlug,
            carDetails.interiorColourSlug,
            []
        ).then(({ features }) => {
            setExteriorColors(features.exteriorColors);
            setInteriorColors(features.interiorColors);
            setMotorizations(features.engines);
            setIsLoading(false);
        });
    }, []);

    const handleColorClick = async (selectedColorSlug: string) => {
        setIsLoading(true);

        const { vehicle, features } = await CarDetailsService.getCarDetailsWithAllOptions(
            carDetails.nameplateBodyStyleSlug,
            carDetails.specPackSlug,
            carDetails.engineGearboxFuelSlug,
            selectedColorSlug,
            carDetails.interiorColourSlug,
            []
        );
        setExteriorColors(features.exteriorColors);
        setInteriorColors(features.interiorColors);
        setMotorizations(features.engines);
        setCarDetails(vehicle);
        dispatch(CarDetailsDuck.setCurrentCarDetails(vehicle));

        setIsLoading(false);
    };

    const selectedCarConfiguration = defaultConfiguration ? defaultConfiguration : selectedCarDetails;
    const images = selectedCarConfiguration?.images?.filter((img: any) => img.type === 'Ext') ?? [{}];
    const mainImage = images?.splice(1, 1) ?? [{}];

    return (
        <div className={className}>
            <div className="title">
                <span>{getModelTitle(carDetails, shouldIncludeBodyStyleDescription)}</span>
                <span className="pack">{carDetails?.specPack?.title}</span>
            </div>
            <div className="keys">
                <span className="label">{t('modal.features.keys.title')}</span>
                <div className="categories">
                    <span
                        className={activeSection === KEY_ALL ? 'selected' : ''}
                        onClick={() => setActiveSection(KEY_ALL)}
                    >
                        {t('modal.features.keys.all')}
                    </span>
                    <span
                        className={activeSection === KEY_ENGINES ? 'selected' : ''}
                        onClick={() => setActiveSection(KEY_ENGINES)}
                    >
                        {t('modal.features.keys.engine')}
                    </span>
                    <span
                        className={activeSection === KEY_COLORS ? 'selected' : ''}
                        onClick={() => setActiveSection(KEY_COLORS)}
                    >
                        {t('modal.features.keys.color')}
                    </span>
                    <span
                        className={activeSection === KEY_INTERIORS ? 'selected' : ''}
                        onClick={() => setActiveSection(KEY_INTERIORS)}
                    >
                        {t('modal.features.keys.interior')}
                    </span>
                    <span
                        className={activeSection === KEY_FEATURES ? 'selected' : ''}
                        onClick={() => setActiveSection(KEY_FEATURES)}
                    >
                        {t('modal.features.keys.feature')}
                    </span>
                </div>
            </div>
            {(activeSection === KEY_ALL || activeSection === KEY_ENGINES) && (
                <div className="engines">
                    <span className="label">{t('modal.features.engine.title')}</span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="configurations">
                            {motorizations?.map((motorization: IFeature, key: number) => {
                                const getPower = () =>
                                    motorization?.characteristics?.find(
                                        (char: { key: string }) => char.key === 'maximumPowerKW'
                                    )?.value;

                                const enginePrice = getOptionMonthlyPrice(motorization);
                                const isSelected =
                                    motorization.engineGearboxFuelSlug === carDetails.engineGearboxFuelSlug;

                                return (
                                    <div key={key}>
                                        <span className="configuration">{motorization.title}</span>
                                        <span className="engine">
                                            {`${motorization.fuel}, ${motorization.gearboxTitle} ${
                                                motorization?.characteristics?.length >= 1
                                                    ? `, ${getPower()} ${t('configurator.info.power.label')}`
                                                    : ''
                                            }`}
                                        </span>
                                        <span className="price">
                                            {formatMoney(
                                                isSOLEnabled
                                                    ? getTotalPriceObjByType(
                                                          motorization.pricesV2,
                                                          IPriceV2Types.TOTAL_B2C_CASH
                                                      )?.breakdown.finalCustomerCashPriceOnTheRoad
                                                    : getTotalPriceObjByType(
                                                          motorization.pricesV2,
                                                          IPriceV2Types.TOTAL_B2C_CASH
                                                      )?.finalPriceInclTax,
                                                true
                                            )}{' '}
                                            {(enginePrice.price || enginePrice.defaultPrice) && (
                                                <span>
                                                    <span className="or">{t('configurator.optionPrice.suffix')}</span>{' '}
                                                    {formatMoney(enginePrice.price || enginePrice.defaultPrice, true)}{' '}
                                                    <PriceSuffix isMonthlyPrice hideAsterisk />
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {(activeSection === KEY_ALL || activeSection === KEY_COLORS) && (
                <div className="colors">
                    <span className="label">{t('modal.features.color.title')}</span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="configurations">
                            {mainImage && <img className="carImage" src={mainImage[0].url} />}
                            <div>
                                {exteriorColors?.map(
                                    (
                                        clr: { id: string; images: { url: string }[]; exteriorColourSlug: string },
                                        key: number
                                    ) => {
                                        return (
                                            <button
                                                className={`colorButton enabled ${
                                                    selectedCarConfiguration?.exteriorColourSlug ===
                                                    clr.exteriorColourSlug
                                                        ? 'selected'
                                                        : ''
                                                }`}
                                                role="button"
                                                key={key}
                                                style={{ backgroundImage: `url('${clr.images[0]?.url}')` }}
                                                onClick={() => handleColorClick(clr.exteriorColourSlug)}
                                            />
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {(activeSection === KEY_ALL || activeSection === KEY_INTERIORS) && (
                <div className="interiors">
                    <span className="label">{t('modal.features.interior.title')}</span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="configurations">
                            {interiorColors?.map((interior: { title: string }, key: number) => (
                                <span key={key}>{interior.title}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {(activeSection === KEY_ALL || activeSection === KEY_FEATURES) && (
                <div className="features">
                    <span className="title">{t('modal.features.feature.title')}</span>
                    <p className="description">{t('modal.features.feature.description')}</p>
                    <div className="categories">
                        <span
                            className={activeFeature === KEY_ALL ? 'selected' : ''}
                            onClick={() => setActiveFeature(KEY_ALL)}
                        >
                            {t('modal.features.feature.all')}
                        </span>
                        {orderBy(carDetails?.standardFeatures, ['category'], ['asc']).map(
                            (feature: { key: string; category: string }, key: number) => (
                                <span
                                    key={key}
                                    className={activeFeature === feature.key ? 'selected' : ''}
                                    onClick={() => setActiveFeature(feature.key)}
                                >
                                    {feature.category}
                                </span>
                            )
                        )}
                    </div>
                    <div className="configurations">
                        {carDetails?.standardFeatures.map(
                            (feature: { key: string; category: string; data: { label: string }[] }, key: number) => {
                                if (activeFeature === KEY_ALL || activeFeature === feature.key) {
                                    return (
                                        <div key={key} className="configuration">
                                            <span className="label">{feature.category}</span>
                                            <div className="options">
                                                {feature.data.map((configuration, key) => (
                                                    <span key={`${configuration.label}-${key}`} className="option">
                                                        {configuration.label}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                            }
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModalFeaturesTemplate;
